import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import WarningIcon from '@material-ui/icons/Warning';
import AddIcon from '@material-ui/icons/Add';

import { WARNING_TYPE_CHOICES, isHrOrManager } from '../../../helpers';
import { getUser } from '../../../user';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: 16,
    },
    activeRow: {
      backgroundColor: 'rgba(244, 67, 54, 0.06)',
    },
    badge: {
      marginRight: theme.spacing(2),
    },
  };
});

const typeLabel = (id) => {
  const found = WARNING_TYPE_CHOICES.find((c) => {
    return c.id === id;
  });
  return found ? found.name : id;
};

const emptyDraft = (silaeId) => {
  return { silae_id: silaeId, type: '', note: '' };
};

// cleaner_warning (plan 013 B2): RBAC-gated section. The backend already 403s
// the resource for non-RH/manager users; this component additionally hides the
// whole section client-side (STOP condition: never expose warnings on a cleaner
// profile). Warnings are audited and never hard-deleted — they are resolved.
const WarningsCard = ({ silaeId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();

  // Fail closed: rely on react-admin permissions, fall back to the stored user.
  const allowed = isHrOrManager(permissions || getUser());

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft(silaeId));
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');

  const reload = () => {
    if (!silaeId || !allowed) {
      setRows([]);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_warning', {
        sort: { field: 'created_at', order: 'DESC' },
        pagination: { page: 1, perPage: 100 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (!cancelled) setRows(data || []);
      })
      .catch(() => {
        // 403 for a non-authorized role lands here; keep the section empty.
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(reload, [silaeId, dataProvider, allowed]);

  // STOP guardrail: render nothing for non RH/managers.
  if (!allowed) {
    return null;
  }

  const activeCount = rows.filter((r) => {
    return r.active;
  }).length;

  const setField = (source, value) => {
    setDraft((prev) => {
      return { ...prev, [source]: value };
    });
  };

  const handleCreate = async () => {
    if (!silaeId || !draft.type || !draft.note) return;
    setSaving(true);
    try {
      const user = permissions || getUser();
      await dataProvider.create('cleaner_warning', {
        data: {
          silae_id: silaeId,
          type: draft.type,
          note: draft.note,
          active: true,
          created_by: user && user.id ? user.id : undefined,
        },
      });
      notify('Signalement enregistré', 'info');
      setOpen(false);
      setDraft(emptyDraft(silaeId));
      reload();
    } catch (err) {
      notify(err.message || "Échec de l'enregistrement", 'warning');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    if (!resolving) return;
    setSaving(true);
    try {
      const user = permissions || getUser();
      await dataProvider.update('cleaner_warning', {
        id: resolving.id,
        data: {
          active: false,
          resolution_note: resolutionNote || null,
          resolved_by: user && user.id ? user.id : undefined,
          resolved_at: new Date().toISOString(),
        },
        previousData: resolving,
      });
      notify('Signalement levé', 'info');
      setResolving(null);
      setResolutionNote('');
      reload();
    } catch (err) {
      notify(err.message || 'Échec de la levée', 'warning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <Card>
        <CardHeader
          avatar={
            <Badge
              className={classes.badge}
              color="error"
              badgeContent={activeCount}
              invisible={activeCount === 0}
            >
              <WarningIcon
                style={{ color: activeCount > 0 ? '#f44336' : undefined }}
              />
            </Badge>
          }
          title="Signalements"
          subheader="Visible RH / managers uniquement"
          action={
            <Button
              size="small"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => {
                return setOpen(true);
              }}
              disabled={!loaded || !silaeId}
            >
              Signaler
            </Button>
          }
        />
        <CardContent>
          {silaeId && loaded && rows.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Aucun signalement.
            </Typography>
          )}
          {rows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>État</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  return (
                    <TableRow
                      key={row.id}
                      className={row.active ? classes.activeRow : undefined}
                    >
                      <TableCell>{typeLabel(row.type)}</TableCell>
                      <TableCell>{row.note}</TableCell>
                      <TableCell>
                        {row.active ? (
                          <Chip size="small" color="secondary" label="Actif" />
                        ) : (
                          <Chip size="small" label="Levé" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.active && (
                          <Button
                            size="small"
                            onClick={() => {
                              setResolving(row);
                              setResolutionNote('');
                            }}
                          >
                            Lever
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create */}
      <Dialog
        open={open}
        onClose={() => {
          return setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nouveau signalement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                label="Type"
                fullWidth
                value={draft.type}
                onChange={(e) => {
                  return setField('type', e.target.value);
                }}
              >
                {WARNING_TYPE_CHOICES.map((c) => {
                  return (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            {draft.type === 'LANGUAGE_BARRIER' && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Motif de <strong>compétence professionnelle</strong> : la note
                  doit rester factuelle et opérationnelle (ex.&nbsp;: «&nbsp;ne
                  comprend pas les consignes de sécurité en français&nbsp;»). Ne
                  mentionnez <strong>jamais</strong> l’origine ni la nationalité
                  de la personne (donnée sensible interdite).
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Justification (obligatoire)"
                fullWidth
                multiline
                minRows={3}
                value={draft.note}
                onChange={(e) => {
                  return setField('note', e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setOpen(false);
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            color="secondary"
            variant="contained"
            disabled={saving || !draft.type || !draft.note}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve */}
      <Dialog
        open={!!resolving}
        onClose={() => {
          return setResolving(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Lever le signalement</DialogTitle>
        <DialogContent>
          <TextField
            label="Note de résolution"
            fullWidth
            multiline
            minRows={3}
            value={resolutionNote}
            onChange={(e) => {
              return setResolutionNote(e.target.value);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setResolving(null);
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            onClick={handleResolve}
            color="primary"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Lever
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

WarningsCard.propTypes = {
  silaeId: PropTypes.string,
};

WarningsCard.defaultProps = {
  silaeId: '',
};

export default WarningsCard;
